import React from "react";
import { motion } from "framer-motion";
import { CheckIcon, UserIcon, EnvelopeIcon, IdentificationIcon } from "@heroicons/react/24/outline";

interface Step {
  id: number;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
}

interface StepProgressProps {
  currentStep: number;
  totalSteps: number;
  userRole?: string;
}

const StepProgress: React.FC<StepProgressProps> = ({ currentStep, totalSteps, userRole = "client" }) => {
  const steps: Step[] = [
    {
      id: 1,
      title: "Compte",
      description: "Informations de base",
      icon: UserIcon,
    },
    {
      id: 2,
      title: "Vérification",
      description: "Confirmer votre email",
      icon: EnvelopeIcon,
    },
    {
      id: 3,
      title: "Profil",
      description: userRole === "client" ? "Profil client" : "Profil prestataire",
      icon: IdentificationIcon,
    },
  ];

  const getStepStatus = (stepId: number) => {
    if (stepId < currentStep) return "completed";
    if (stepId === currentStep) return "current";
    return "upcoming";
  };

  const getStepStyles = (status: string) => {
    switch (status) {
      case "completed":
        return {
          circle: "bg-green-500 border-green-500 text-white shadow-lg shadow-green-200",
          line: "bg-green-500",
          text: "text-green-600",
          description: "text-green-500",
        };
      case "current":
        return {
          circle: "bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-200 ring-4 ring-blue-100",
          line: "bg-gray-300",
          text: "text-blue-600 font-semibold",
          description: "text-blue-500",
        };
      default:
        return {
          circle: "bg-gray-100 border-gray-300 text-gray-400",
          line: "bg-gray-300",
          text: "text-gray-400",
          description: "text-gray-400",
        };
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto px-4 py-8">
      {/* Mobile version */}
      <div className="md:hidden">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm text-gray-500">
            Étape {currentStep} sur {totalSteps}
          </span>
          <span className="text-sm font-medium text-gray-900">
            {Math.round((currentStep / totalSteps) * 100)}% terminé
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
          <motion.div
            className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${(currentStep / totalSteps) * 100}%` }}
            transition={{ duration: 0.5, ease: "easeOut" }}
          />
        </div>
        <div className="text-center">
          <h3 className="text-lg font-semibold text-gray-900 mb-1">{steps[currentStep - 1]?.title}</h3>
          <p className="text-sm text-gray-500">{steps[currentStep - 1]?.description}</p>
        </div>
      </div>

      {/* Desktop version */}
      <div className="hidden md:block">
        <nav aria-label="Progress" className="mb-8">
          <ol className="flex items-center justify-between">
            {steps.map((step, stepIdx) => {
              const status = getStepStatus(step.id);
              const styles = getStepStyles(status);
              const Icon = step.icon;

              return (
                <li key={step.id} className="relative flex flex-col items-center flex-1">
                  {/* Connection line */}
                  {stepIdx < steps.length - 1 && (
                    <div className="absolute top-5 left-1/2 w-full h-0.5 -ml-px">
                      <motion.div
                        className={`h-full ${styles.line}`}
                        initial={{ scaleX: status === "completed" ? 0 : 1 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 0.5, delay: status === "completed" ? 0.2 : 0 }}
                      />
                    </div>
                  )}

                  {/* Step circle */}
                  <motion.div
                    className={`relative z-10 flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-300 ${styles.circle}`}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.3, delay: stepIdx * 0.1 }}
                    whileHover={{ scale: 1.1 }}
                  >
                    {status === "completed" ? (
                      <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.3, delay: 0.2 }}
                      >
                        <CheckIcon className="w-5 h-5" />
                      </motion.div>
                    ) : status === "current" ? (
                      <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ duration: 0.3 }}>
                        <Icon className="w-5 h-5" />
                      </motion.div>
                    ) : (
                      <Icon className="w-5 h-5" />
                    )}
                  </motion.div>

                  {/* Step content */}
                  <div className="mt-4 text-center max-w-24">
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: stepIdx * 0.1 + 0.2 }}
                    >
                      <p className={`text-sm font-medium ${styles.text}`}>{step.title}</p>
                      <p className={`text-xs mt-1 ${styles.description}`}>{step.description}</p>
                    </motion.div>
                  </div>
                </li>
              );
            })}
          </ol>
        </nav>
      </div>
    </div>
  );
};

export default StepProgress;
